import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Dropdown from 'antd/lib/dropdown';
import Empty from 'antd/lib/empty';
import Input from 'antd/lib/input';
import Popover from 'antd/lib/popover';
import Spin from 'antd/lib/spin';
import Tabs from 'antd/lib/tabs';
import Tooltip from 'antd/lib/tooltip';
import {
    AimOutlined,
    CheckSquareOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    FilterOutlined,
    FolderFilled,
    InfoCircleOutlined,
    LoadingOutlined,
    PlusOutlined,
    RocketOutlined,
    SearchOutlined,
    ToolFilled,
    UndoOutlined,
} from '@ant-design/icons';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '@/components/styles/globalStyles';
import { TriageIssueCard } from './TriageIssueCard';
import { PAYWALL_ISSUES } from './paywallIssues';
import { CreateIssueModal } from './CreateIssueModal';
import { MergeIssuesModal } from './MergeIssuesModal';
import { TriageFilters } from './TriageFilters';
import { tryFetchPaginatedEntities } from '@/actions/entities';
import {
    clearPinnedIssue,
    setTriageReleaseFilter,
    setTriageSearch,
    setTriageSortBy,
    setTriageStatusGroup,
    TRY_BULK_TRANSITION_ISSUES,
    TRY_BULK_REOPEN_ISSUES,
    tryBulkTransitionIssues,
    tryBulkReopenIssues,
    TriageReleaseFilter,
    TriageSortBy,
    TriageStatusGroup,
    TriageViewType,
} from '@/actions/triage';
import { FetchableEntityType, FetchablePaginatedEntityType } from '@/entityType';
import { RootState } from '@/reducers/reducers';
import {
    selectAppHome,
    selectAppIssues,
    selectIssuesCounts,
    selectPaginatedIssuesLimitAndOffset,
    selectRelease,
    selectTriageFilters,
} from '@/selectors/selectors';
import { useGetEntity, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { AppComponentProps, FeatureSection } from '@/typescriptHelpers';
import { IssueState, SubscriptionFeatureType } from '@/types';
import { EmptyStateWrapper } from '@/components/releaseEmptyState/EmptyStateWrapper';
import {
    useFeaturePlanRestrictionTooltipContent,
    useRestrictedFeatureIsDisabled,
} from '@/components/planRestrictions/planRestrictionHelperComponents';

const SORT_OPTIONS: { value: TriageSortBy; label: string }[] = [
    { value: 'triage', label: 'Triage' },
    { value: 'date', label: 'Date' },
    { value: 'destinations', label: 'Destination' },
    { value: 'owner', label: 'Owner' },
    { value: 'source', label: 'Source' },
];

const sortLabel = (sortBy: TriageSortBy): string => SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Triage';

const PAGE_SIZE = 20;

const filterButtonTextStyle: React.CSSProperties = { color: antGrey.grey8 };
const filterButtonIconStyle: React.CSSProperties = { color: rwColors.primary };

const tabLabel = (label: string, count: number, isActive: boolean, tooltip?: string) => (
    <span style={{ fontWeight: 600, fontSize: 16, color: isActive ? rwColors.primary : antGrey.grey7 }}>
        {label}{' '}
        <span style={{ fontWeight: 400, color: isActive ? colorWithAlpha(rwColors.primary, 0.5) : antGrey.grey5 }}>
            - {count}
        </span>
        {tooltip && (
            <Tooltip title={tooltip} placement="top">
                <InfoCircleOutlined
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginLeft: 6, color: antGrey.grey6, fontSize: 14 }}
                />
            </Tooltip>
        )}
    </span>
);

export const TriageIssuesList: React.FC<AppComponentProps & { releaseId?: string; basePath: string }> = ({
    orgId,
    appId,
    releaseId,
    basePath,
}) => {
    const dispatch = useDispatch();

    const shouldShowPaywall = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Triage);
    const paywallContent = useFeaturePlanRestrictionTooltipContent(SubscriptionFeatureType.Triage);

    const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = React.useState(false);

    // Multi-select mode: entered via the Select button. Tracks which issue ids are checked.
    const [isSelectMode, setIsSelectMode] = React.useState(false);
    const [selectedIssueIds, setSelectedIssueIds] = React.useState<Set<string>>(new Set());
    const [isGroupModalOpen, setIsGroupModalOpen] = React.useState(false);
    const [isEscalateMode, setIsEscalateMode] = React.useState(false);
    const openGroupModal = (escalate: boolean) => {
        setIsEscalateMode(escalate);
        setIsGroupModalOpen(true);
    };
    const toggleIssueSelected = (id: string) =>
        setSelectedIssueIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    const cancelSelect = () => {
        setIsSelectMode(false);
        setSelectedIssueIds(new Set());
    };

    const viewType = releaseId ? TriageViewType.Release : TriageViewType.App;
    const isBulkTransitioning = useIsLoading(TRY_BULK_TRANSITION_ISSUES, appId);
    const bulkTransitionDone = useUpdated(TRY_BULK_TRANSITION_ISSUES, appId);
    // Clear the selection after a bulk action but stay in select mode so the user can keep acting.
    React.useEffect(() => {
        if (bulkTransitionDone) {
            setSelectedIssueIds(new Set());
        }
    }, [bulkTransitionDone]);
    const onBulkNotAPriority = () =>
        dispatch(
            tryBulkTransitionIssues({
                orgId,
                appId,
                issueIds: Array.from(selectedIssueIds),
                targetState: IssueState.NotAPriority,
                viewType,
            }),
        );

    const isBulkReopening = useIsLoading(TRY_BULK_REOPEN_ISSUES, appId);
    const bulkReopenDone = useUpdated(TRY_BULK_REOPEN_ISSUES, appId);
    React.useEffect(() => {
        if (bulkReopenDone) {
            setSelectedIssueIds(new Set());
        }
    }, [bulkReopenDone]);
    const onBulkReopen = () =>
        dispatch(tryBulkReopenIssues({ orgId, appId, issueIds: Array.from(selectedIssueIds), viewType }));

    const filters = useSelector((state: RootState) => selectTriageFilters(state, appId));

    // Local input value drives the field immediately; the committed search (which triggers the refetch) is debounced.
    const [searchInput, setSearchInput] = React.useState(filters.search);
    const searchTimer = React.useRef<ReturnType<typeof setTimeout>>();
    React.useEffect(() => () => clearTimeout(searchTimer.current), []);
    const onSearchChange = (next: string) => {
        setSearchInput(next);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => dispatch(setTriageSearch({ appId, search: next.trim() })), 300);
    };
    const commitSearch = (next: string) => {
        clearTimeout(searchTimer.current);
        dispatch(setTriageSearch({ appId, search: next.trim() }));
    };

    const issues = useSelector((state: RootState) => selectAppIssues(state, appId));
    const { offset } = useSelector((state: RootState) => selectPaginatedIssuesLimitAndOffset(state, appId));

    const selectedIssues = issues.filter((issue) => selectedIssueIds.has(issue.id));
    // An issue with a fix request can't be grouped with another fix-request issue (one fix request only). Once one
    // is selected, the other fix-request issues' checkboxes are disabled, and the Group buttons swap for "Merge
    // into existing issue with fix request".
    const selectionHasFixRequest = selectedIssues.some((issue) => !!issue.fixRequest);

    // Live/Next release versions back the release filter buttons. Ensure appHome is loaded for them.
    const appHome = useSelector((state: RootState) => selectAppHome(state, appId));
    const liveVersion = appHome?.liveRelease?.version;
    const nextVersion = appHome?.nextRelease?.version;
    const [fetchAppHome] = useGetEntity(FetchableEntityType.AppHome, appId);
    React.useEffect(() => {
        fetchAppHome({ orgId, appId });
    }, [orgId, appId]);

    // At the release-level route, always scope to the release we're viewing. Otherwise the Live/Next
    // buttons take precedence, and falling back to the explicit Version filter from the popover.
    const releaseVersion = useSelector((state: RootState) =>
        releaseId ? selectRelease(state, releaseId)?.version : undefined,
    );
    let activeVersions: string[] = filters.versions;

    if (releaseId) {
        activeVersions = releaseVersion ? [releaseVersion] : [];
    } else if (filters.releaseFilter === 'live') {
        activeVersions = liveVersion ? [liveVersion] : [];
    } else if (filters.releaseFilter === 'next') {
        activeVersions = nextVersion ? [nextVersion] : [];
    }

    // The explicit Version filter only applies (and is shown) at app level with no Live/Next selected.
    const isVersionFilterActive = !releaseId && filters.releaseFilter === 'none';

    const appliedFilterCount =
        filters.statuses.length +
        filters.sources.length +
        filters.owners.length +
        filters.reporters.length +
        (isVersionFilterActive ? filters.versions.length : 0);

    const fetchIssues = React.useCallback(
        (statusGroup: TriageStatusGroup, pageOffset: number) => {
            dispatch(
                tryFetchPaginatedEntities(
                    FetchablePaginatedEntityType.Issues,
                    {
                        orgId,
                        appId,
                        statusGroup,
                        statuses: filters.statuses,
                        sources: filters.sources,
                        owners: filters.owners,
                        reporters: filters.reporters,
                        versions: activeVersions,
                        destinations: filters.destinations,
                        sortBy: filters.sortBy,
                        search: filters.search || undefined,
                    },
                    PAGE_SIZE,
                    pageOffset,
                    appId,
                ),
            );
        },
        [
            dispatch,
            orgId,
            appId,
            filters.statuses,
            filters.sources,
            filters.owners,
            filters.reporters,
            filters.destinations,
            filters.sortBy,
            filters.search,
            activeVersions.join(','),
        ],
    );

    React.useEffect(() => {
        if (shouldShowPaywall) return;
        dispatch(clearPinnedIssue({ appId }));
        fetchIssues(filters.statusGroup, 0);
    }, [filters.statusGroup, fetchIssues, shouldShowPaywall, dispatch, appId]);

    const toggleReleaseFilter = (target: TriageReleaseFilter) => {
        // Re-clicking the active filter clears it; the two are mutually exclusive.
        dispatch(setTriageReleaseFilter({ appId, releaseFilter: filters.releaseFilter === target ? 'none' : target }));
    };

    const releaseFilterButtonStyle = (active: boolean, disabled?: boolean): React.CSSProperties =>
        disabled ?
            { color: antGrey.grey8 }
        :   {
                color: active ? runwayColors.white : antGrey.grey8,
                backgroundColor: active ? rwColors.primary : runwayColors.white,
                borderColor: active ? rwColors.primary : antGrey.grey5,
            };

    const onLoadMore = () => fetchIssues(filters.statusGroup, offset ?? 0);

    const isFetchingIssues = useIsLoading(FetchablePaginatedEntityType.Issues, appId);

    const onTabChange = (key: string) => {
        dispatch(setTriageStatusGroup({ appId, statusGroup: key as TriageStatusGroup }));
    };

    const counts = useSelector((state: RootState) => selectIssuesCounts(state, appId));
    const { openCount, closedCount } =
        shouldShowPaywall ? { openCount: PAYWALL_ISSUES.length, closedCount: 0 } : counts;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '24px 32px 32px' }}>
            <Tabs
                className="triage-issues-tabs"
                activeKey={filters.statusGroup}
                onChange={onTabChange}
                items={[
                    {
                        key: 'open',
                        label: tabLabel('Open', openCount, filters.statusGroup === 'open'),
                    },
                    {
                        key: 'closed',
                        disabled: shouldShowPaywall,
                        label: tabLabel(
                            'Closed',
                            closedCount,
                            filters.statusGroup === 'closed',
                            'Issues are considered closed if they have one of the following statuses: Not a priority, fix rejected, fix approved, or resolved.',
                        ),
                    },
                ]}
                tabBarExtraContent={{
                    right: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isSelectMode ?
                                <Button
                                    icon={<CloseOutlined style={{ color: runwayColors.statusRed }} />}
                                    onClick={cancelSelect}
                                    style={filterButtonTextStyle}
                                >
                                    Cancel select
                                </Button>
                            :   <>
                                    {!releaseId && (
                                        <>
                                            <Button
                                                size="small"
                                                disabled={shouldShowPaywall || !liveVersion}
                                                onClick={() => toggleReleaseFilter('live')}
                                                icon={
                                                    <RocketOutlined
                                                        style={{
                                                            color:
                                                                filters.releaseFilter === 'live' ?
                                                                    runwayColors.white
                                                                :   antGrey.grey6,
                                                        }}
                                                    />
                                                }
                                                style={releaseFilterButtonStyle(
                                                    filters.releaseFilter === 'live',
                                                    shouldShowPaywall,
                                                )}
                                            >
                                                Live release
                                            </Button>
                                            <Button
                                                size="small"
                                                disabled={shouldShowPaywall || !nextVersion}
                                                onClick={() => toggleReleaseFilter('next')}
                                                icon={
                                                    <ClockCircleOutlined
                                                        style={{
                                                            color:
                                                                filters.releaseFilter === 'next' ?
                                                                    runwayColors.white
                                                                :   antGrey.grey6,
                                                        }}
                                                    />
                                                }
                                                style={releaseFilterButtonStyle(
                                                    filters.releaseFilter === 'next',
                                                    shouldShowPaywall,
                                                )}
                                            >
                                                Next release
                                            </Button>
                                            <Divider
                                                type="vertical"
                                                style={{ height: 40, borderColor: antGrey.grey4 }}
                                            />
                                        </>
                                    )}
                                    <Button
                                        icon={<CheckSquareOutlined style={filterButtonIconStyle} />}
                                        style={filterButtonTextStyle}
                                        disabled={shouldShowPaywall}
                                        onClick={() => setIsSelectMode(true)}
                                    >
                                        Select
                                    </Button>
                                    <Dropdown
                                        trigger={['click']}
                                        disabled={shouldShowPaywall}
                                        menu={{
                                            items: SORT_OPTIONS.map((opt) => ({ key: opt.value, label: opt.label })),
                                            selectedKeys: [filters.sortBy],
                                            onClick: ({ key }) =>
                                                dispatch(setTriageSortBy({ appId, sortBy: key as TriageSortBy })),
                                        }}
                                    >
                                        <Button
                                            icon={<AimOutlined style={filterButtonIconStyle} />}
                                            style={filterButtonTextStyle}
                                            disabled={shouldShowPaywall}
                                        >
                                            Sort: {sortLabel(filters.sortBy)}
                                        </Button>
                                    </Dropdown>
                                    <Badge
                                        count={appliedFilterCount}
                                        size="small"
                                        style={{ backgroundColor: rwColors.primary }}
                                    >
                                        <Popover
                                            trigger="click"
                                            placement="bottomRight"
                                            destroyTooltipOnHide
                                            overlayInnerStyle={{ padding: 0, overflow: 'hidden' }}
                                            open={isFilterPopoverOpen}
                                            onOpenChange={setIsFilterPopoverOpen}
                                            content={
                                                <TriageFilters
                                                    orgId={orgId}
                                                    appId={appId}
                                                    showVersionFilter={isVersionFilterActive}
                                                    onApply={() => setIsFilterPopoverOpen(false)}
                                                />
                                            }
                                        >
                                            <Button
                                                icon={<FilterOutlined style={filterButtonIconStyle} />}
                                                style={filterButtonTextStyle}
                                                disabled={shouldShowPaywall}
                                            >
                                                Filter
                                            </Button>
                                        </Popover>
                                    </Badge>
                                </>
                            }
                        </div>
                    ),
                }}
            />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    marginTop: 8,
                }}
            >
                <div style={{ flex: '0 1 875px', minWidth: 0 }}>
                    <Input.Search
                        placeholder="Search"
                        enterButton={
                            <Button
                                disabled={shouldShowPaywall}
                                icon={
                                    <SearchOutlined
                                        style={{ color: shouldShowPaywall ? antGrey.grey5 : antGrey.grey7 }}
                                    />
                                }
                            />
                        }
                        allowClear
                        disabled={shouldShowPaywall}
                        value={searchInput}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onSearch={commitSearch}
                        style={{ width: '100%' }}
                    />
                </div>
                {isSelectMode ?
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        {filters.statusGroup === 'closed' ?
                            <Button
                                icon={
                                    <UndoOutlined
                                        style={{
                                            color: selectedIssueIds.size < 1 ? undefined : rwColors.statusYellow,
                                        }}
                                    />
                                }
                                disabled={selectedIssueIds.size < 1}
                                loading={isBulkReopening}
                                onClick={onBulkReopen}
                            >
                                Reopen
                            </Button>
                        :   <>
                                {selectionHasFixRequest ?
                                    <Button
                                        type="primary"
                                        icon={<FolderFilled />}
                                        disabled={selectedIssueIds.size < 2}
                                        onClick={() => openGroupModal(false)}
                                        style={{
                                            backgroundColor: rwColors.primary,
                                            borderColor: rwColors.primary,
                                            color: runwayColors.white,
                                            opacity: selectedIssueIds.size < 2 ? 0.5 : undefined,
                                        }}
                                    >
                                        Merge into existing issue with fix request
                                    </Button>
                                :   <>
                                        <Button
                                            type="primary"
                                            icon={<FolderFilled />}
                                            disabled={selectedIssueIds.size < 2}
                                            onClick={() => openGroupModal(false)}
                                            style={{
                                                backgroundColor: rwColors.primary,
                                                borderColor: rwColors.primary,
                                                color: runwayColors.white,
                                                opacity: selectedIssueIds.size < 2 ? 0.5 : undefined,
                                            }}
                                        >
                                            Merge issues
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<ToolFilled />}
                                            disabled={selectedIssueIds.size < 2}
                                            onClick={() => openGroupModal(true)}
                                            style={{
                                                backgroundColor: rwColors.primary,
                                                borderColor: rwColors.primary,
                                                color: runwayColors.white,
                                                opacity: selectedIssueIds.size < 2 ? 0.5 : undefined,
                                            }}
                                        >
                                            Merge and escalate to fix request
                                        </Button>
                                    </>
                                }
                                <Button
                                    icon={<CloseCircleOutlined style={{ color: runwayColors.white }} />}
                                    disabled={selectedIssueIds.size < 1}
                                    loading={isBulkTransitioning}
                                    onClick={onBulkNotAPriority}
                                    style={{
                                        backgroundColor: antGrey.grey7,
                                        color: runwayColors.white,
                                        borderColor: antGrey.grey7,
                                        opacity: selectedIssueIds.size < 1 ? 0.5 : undefined,
                                    }}
                                >
                                    Not a priority
                                </Button>
                            </>
                        }
                    </div>
                :   <Button
                        icon={<PlusOutlined style={{ color: shouldShowPaywall ? antGrey.grey6 : rwColors.primary }} />}
                        disabled={shouldShowPaywall}
                        onClick={() => setIsCreateModalOpen(true)}
                        style={{
                            marginLeft: 'auto',
                            color: shouldShowPaywall ? antGrey.grey6 : rwColors.primary,
                            backgroundColor: shouldShowPaywall ? antGrey.grey3 : colorWithAlpha(rwColors.primary, 0.1),
                            borderColor: 'transparent',
                            opacity: shouldShowPaywall ? 0.6 : undefined,
                            paddingLeft: 32,
                            paddingRight: 32,
                            visibility: filters.statusGroup === 'open' ? 'visible' : 'hidden',
                        }}
                    >
                        Add a new issue
                    </Button>
                }
            </div>
            {shouldShowPaywall ?
                <EmptyStateWrapper
                    featureSections={[FeatureSection.Triage]}
                    shouldForceEnable
                    shouldCenterContent
                    appId={appId}
                    releaseId={null}
                    paywallContent={<div className="org-overview-paywall-content">{paywallContent}</div>}
                >
                    <div style={{ paddingTop: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                                opacity: 0.5,
                                pointerEvents: 'none',
                            }}
                        >
                            {PAYWALL_ISSUES.map((issue) => (
                                <TriageIssueCard
                                    key={issue.id}
                                    issue={{ ...issue, appId }}
                                    orgId={orgId}
                                    viewType={viewType}
                                    disabled
                                />
                            ))}
                        </div>
                    </div>
                </EmptyStateWrapper>
            :   <>
                    {isFetchingIssues && (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 16 }}>
                            <Spin
                                indicator={<LoadingOutlined style={{ fontSize: 18, color: rwColors.primary }} spin />}
                            />
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 24 }}>
                        {issues.map((issue) => (
                            <TriageIssueCard
                                key={issue.id}
                                issue={issue}
                                orgId={orgId}
                                viewType={viewType}
                                basePath={basePath}
                                selectMode={isSelectMode}
                                selected={selectedIssueIds.has(issue.id)}
                                selectDisabled={
                                    selectionHasFixRequest && !!issue.fixRequest && !selectedIssueIds.has(issue.id)
                                }
                                onToggleSelected={() => toggleIssueSelected(issue.id)}
                            />
                        ))}
                        {!isFetchingIssues && issues.length === 0 && (
                            <Empty
                                className="triage-empty"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    filters.statusGroup === 'open' ? 'No open issues found.' : 'No closed issues found.'
                                }
                                style={{ paddingTop: 64 }}
                            />
                        )}
                        {issues.length >= PAGE_SIZE && (
                            <Button onClick={onLoadMore} style={{ alignSelf: 'center' }}>
                                Load more
                            </Button>
                        )}
                    </div>
                </>
            }
            <CreateIssueModal
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                orgId={orgId}
                appId={appId}
                lockedReleaseId={releaseId}
                statusGroup={filters.statusGroup}
                sources={filters.sources}
                sortBy={filters.sortBy}
            />
            <MergeIssuesModal
                open={isGroupModalOpen}
                onClose={() => setIsGroupModalOpen(false)}
                onGrouped={() => setSelectedIssueIds(new Set())}
                orgId={orgId}
                appId={appId}
                selectedIssues={selectedIssues}
                viewType={viewType}
                escalate={isEscalateMode}
            />
        </div>
    );
};
